import { apiErrorMessage } from '@agos/api-client'
import { tt } from '@agos/shared'

/**
 * Who may store a settings section — the module's one shared rule.
 *
 * Every settings controller in the backend follows the same shape, and it is not the shape either
 * legacy gate expressed:
 *
 * ```java
 * @GetMapping("/api/v1/settings/system")
 * @RequireAuth({"main_admin", "admin"})     // an admin may read
 * @PutMapping("/api/v1/settings/system")
 * @RequireAuth("main_admin")                // only a main admin may write
 * ```
 *
 * This is FB-001's resolution. F0 framed the sidebar-versus-router mismatch as a choice between
 * letting an `admin` in or hiding the item; the server's answer is *both and neither* — let them in,
 * because they may read, and refuse the write, because they may not. So each page admits whoever the
 * server lets read and disables what it will not let them store, saying which.
 *
 * The exceptions are real and few, so they are listed rather than guessed:
 *
 * | Section | Read | Write |
 * |---|---|---|
 * | most of `/settings/*` | `main_admin`, `admin` | **`main_admin`** |
 * | `/hikvision/*` | `main_admin`, `admin` | `main_admin`, `admin` |
 * | `/backups` | `main_admin`, `admin` (+ a `manager` role that does not exist) | same |
 * | `/settings/permissions` | **`main_admin`** | `main_admin` |
 * | `/notifications/channels` and every other rule | `main_admin`, `admin` | **`main_admin`** |
 * | `/notifications/{id}/read`, `/read-all` | `main_admin`, `admin` | same |
 * | `/translations/*` | `main_admin`, `admin` | same |
 */

export type SystemRole = 'main_admin' | 'admin' | 'cook' | 'waiter' | 'driver'

/**
 * The sections an `admin` may write as well as read. Everything else is main-admin-only.
 *
 * Each entry is checked against the controller, not assumed. `hikvision` (`/hikvision/config`, `/test`,
 * `/sync`), `apk` (`/apk/upload-url`, `/confirm`, `/build`) and `backups` (`/backups/trigger`) are all
 * `@RequireAuth({"main_admin", "admin"})`.
 *
 * **`notifications` was on this list and should not have been.** Every rule-changing endpoint on
 * `NotificationsController` — `PUT /notifications/channels/{key}`, `/channel-settings/{channel}`,
 * `/channels/{key}/strategy`, `POST /templates`, `/test-send/{channel}`, `/daily-check` — is
 * `main_admin`. What an `admin` may do is mark their own inbox read (`POST /notifications/{id}/read` and
 * `/read-all`), which is a different section and is listed as one.
 */
export const ADMIN_WRITABLE: readonly string[] = [
  'hikvision',
  'backups',
  'apk',
  'notifications-inbox',
  // `PATCH`/`DELETE /translations/{id}` and `POST /translations/bulk-delete` are all
  // `@RequireAuth({"main_admin", "admin"})` — the translation memory is not a settings blob and does not
  // follow the module's read/write split.
  'translations',
  // §9.5's channel editor. `POST`/`PATCH`/`DELETE /sales-channels` are `{main_admin, admin}`; the `GET`
  // admits every role, because every screen that renders an order has to know what its channel does.
  'sales-channels',
  // §9.5's VAT editor, the same shape: `{main_admin, admin}` on all three writes.
  'vat-categories',
]

/**
 * The sections whose write is gated on a **permission** rather than on a role.
 *
 * `@RequirePermission(value = "opening_hours_edit", orRoles = {"main_admin"})` — so anybody holding the
 * flag may write, whatever their role, and a main admin may anyway. A role check alone would lock out
 * exactly the person the flag was created for.
 */
export const PERMISSION_WRITABLE: Readonly<Record<string, string>> = {
  'opening-hours': 'opening_hours_edit',
}

export function mayWrite(
  section: string,
  role: string | undefined,
  hasPermission?: (permission: string) => boolean,
): boolean {
  if (role === 'main_admin') return true
  const flag = PERMISSION_WRITABLE[section]
  if (flag !== undefined) return hasPermission?.(flag) === true
  if (role === 'admin') return ADMIN_WRITABLE.includes(section)
  return false
}

/** Said on the page, before anything is typed into a field that cannot be stored. */
export function readOnlyReason(
  section: string,
  role: string | undefined,
  hasPermission?: (permission: string) => boolean,
): string | null {
  if (mayWrite(section, role, hasPermission)) return null
  if (PERMISSION_WRITABLE[section] !== undefined) {
    return tt('Na zmenu týchto nastavení potrebujete príslušné právo. Vidieť ich môžete, uložiť nie.')
  }
  if (role === 'admin') {
    return tt(
      'Tieto nastavenia môže uložiť len hlavný administrátor. Vidieť ich môžete, uložiť nie - server zmenu odmietne.',
    )
  }
  return tt('Na uloženie týchto nastavení nemáte právo.')
}

/**
 * Whether a response actually stored anything.
 *
 * **FB-130.** `fetch` resolves for a 403 as happily as for a 200, so legacy's
 *
 * ```js
 * await Promise.all([fetch(…general), fetch(…system), fetch(…basic)])
 * setSaved(true)
 * ```
 *
 * shows the green tick after three refusals. There is no `res.ok` anywhere on the page, and the `catch`
 * only fires on a network error — which a 403 is not. For an `admin`, who may read every one of those
 * sections and write none of them, the tick is *always* false.
 *
 * The generated client throws on a non-2xx, so a mutation's `onError` is the honest place. What this
 * function exists for is the case legacy could not express at all: three independent writes, some of
 * which succeeded.
 */
export interface SaveOutcome {
  /** The sections that stored. */
  saved: readonly string[]
  /** The sections that refused. */
  failed: readonly string[]
  /**
   * What the refusals actually said - §9.392, FB-697.
   *
   * Distinct, because three writes refused by one guard carry one sentence three times and a toast
   * repeating it is noise. Empty when no refusal carried a message, which is a real case: a 403
   * with no body says nothing and the screen has to say so itself.
   */
  reasons: readonly string[]
  state: 'all' | 'partial' | 'none'
}

/**
 * One settled write, as `saveOutcome` wants it - §9.392.
 *
 * Here rather than at the call site so the three lines that use it cannot disagree, and typed off
 * `PromiseSettledResult` so a `fulfilled` result cannot be read for a reason it does not have.
 */
export function settled(result: PromiseSettledResult<unknown> | undefined): {
  ok: boolean
  reason?: unknown
} {
  if (result === undefined) return { ok: false }
  return result.status === 'fulfilled' ? { ok: true } : { ok: false, reason: result.reason }
}

export function saveOutcome(
  results: { section: string; ok: boolean; reason?: unknown }[],
): SaveOutcome {
  const saved = results.filter((result) => result.ok).map((result) => result.section)
  const failed = results.filter((result) => !result.ok).map((result) => result.section)
  // §9.392 - `Promise.allSettled` hands every rejection's `reason` to the caller and this used to
  // reduce it to a boolean, so the server's own words were gone before a message was composed.
  const said = results
    .filter((result) => !result.ok && result.reason !== undefined)
    .map((result) => apiErrorMessage(result.reason, ''))
    .filter((sentence) => sentence !== '')
  return {
    saved,
    failed,
    reasons: [...new Set(said)],
    state: failed.length === 0 ? 'all' : saved.length === 0 ? 'none' : 'partial',
  }
}

/**
 * What to tell somebody after a save that half worked.
 *
 * Legacy has one message for three requests, so a half-written settings page reads as a saved one. A
 * partial save is the state worth naming: the screen and the server now disagree about *some* of it, and
 * which some matters.
 */
export function saveMessage(outcome: SaveOutcome): { title: string; description?: string } {
  if (outcome.state === 'all') return { title: tt('Nastavenia uložené') }
  // §9.392 - the server's sentence where there is one. `Na serveri sa nič nezmenilo` stays for a
  // refusal with an empty body, because that case still has to be named.
  const said = outcome.reasons.join(' ')
  if (outcome.state === 'none') {
    return {
      title: tt('Uloženie zlyhalo'),
      description: said === '' ? tt('Na serveri sa nič nezmenilo.') : said,
    }
  }
  const which = `${tt('Neuložené')}: ${outcome.failed.join(', ')}. ${tt('Obnovte stránku, aby ste videli, čo na serveri naozaj je.')}`
  return {
    title: tt('Uložila sa len časť nastavení'),
    // Appended rather than substituted: *which* half failed is the fact that sends somebody to
    // reload the page, and the reason is why they cannot simply save again.
    description: said === '' ? which : `${which} ${said}`,
  }
}
